// scheduling/components/reports/SvaReport.jsx
// Scheduled vs Actual (Trello #252, PR 2): what was on the calendar vs what was charged,
// for the last 1 / 2 / 4 / 8 full weeks.
//
// Everyone can open it (the first Audience='everyone' report, migrate-080):
//   Me            your own scheduled vs charged + a line that reconciles to your timesheet
//   Jobs I lead   JMs / JPs (their default) — by job or by person
//   Firm          super-users — by job or by person, plus "view as" anyone
//
// ─── THE SERVER DECIDES THE VIEW ────────────────────────────────────────────
// `vview` defaults to '' = "let the server pick" (Jobs for a lead with data, else Me). It is
// NOT defaulted to 'me': useUrlState omits a value equal to its default, so clicking "Me"
// would write nothing, the server would re-default a lead to Jobs, and the toggle would
// bounce. The active pill always reflects the server's `data.view`, which is also the only
// thing that decides what rows come back — this page can ask, never widen.
//
// ─── PRIVACY ────────────────────────────────────────────────────────────────
// In Jobs I lead, a person's row is their time on YOUR jobs only. Their whole-week line
// (admin, PTO, other work) is shown only in their own Me view and to super-users. The server
// enforces that (and asserts it); this page just renders what it is given.
//
// ─── TOOLTIP RULE ───────────────────────────────────────────────────────────
// Every `title` is written for the person reading the report: no table or function names.

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUrlState } from '../../../hooks/useUrlState';
import { fetchSva } from '../../services/reportsApi';
import { BRAND } from '../../utils/schedConstants';
import { useSort, SortableTh, PlainTh } from './SortableTh';
import { exportCsv } from '../../utils/csv';
import { gridJobHref, shortDay } from '../../utils/reportLinks';

const RANGE_OPTIONS = [1, 2, 4, 8];
const RANGE_LABEL = { 1: 'Last week', 2: '2 weeks', 4: '4 weeks', 8: '8 weeks' };
const CHARGED_ONLY_CAP = 200;
const TINY = 1;
const MISS_STYLE = {
  under: { background: '#FEE2E2', color: '#B91C1C' },
  over: { background: '#FEF3C7', color: '#92400E' },
};

const hrs = (n) => {
  const v = Math.round((Number(n) || 0) * 100) / 100;
  return v === 0 ? '' : String(v);
};
const signed = (n) => {
  const v = Math.round((Number(n) || 0) * 100) / 100;
  return v > 0 ? `+${v}` : String(v);
};
const isTiny = (r) => (r.S || 0) < TINY && (r.C || 0) < TINY;

const MAIN_SORT = {
  client: { get: (r) => r.clientCode, dir: 'asc' },
  clientName: { get: (r) => r.clientName, dir: 'asc' },
  job: { get: (r) => r.jobName, dir: 'asc' },
  jm: { get: (r) => r.managerName, dir: 'asc' },
  S: { get: (r) => r.S, dir: 'desc' },
  C: { get: (r) => r.C, dir: 'desc' },
  delta: { get: (r) => r.delta, dir: 'asc' },
  budget: { get: (r) => r.budgetHours || null, dir: 'desc' },
};
const PERSON_SORT = {
  name: { get: (p) => p.name, dir: 'asc' },
  level: { get: (p) => p.level, dir: 'asc' },
  S: { get: (p) => p.S, dir: 'desc' },
  C: { get: (p) => p.C, dir: 'desc' },
  delta: { get: (p) => p.delta, dir: 'asc' },
  sheet: { get: (p) => p.reconcile?.charged?.total ?? null, dir: 'desc' },
};
const CHARGED_PERSON_SORT = {
  name: { get: (p) => p.name, dir: 'asc' },
  level: { get: (p) => p.level, dir: 'asc' },
  C: { get: (p) => p.C, dir: 'desc' },
  jobs: { get: (p) => p.jobCount, dir: 'desc' },
};
const CHARGED_SORT = {
  client: { get: (r) => r.clientCode, dir: 'asc' },
  job: { get: (r) => r.jobName, dir: 'asc' },
  C: { get: (r) => r.C, dir: 'desc' },
};

// One week, read as ACTUAL (charged) / SCHEDULED — actual first, it's the number people look
// for. `noActual` is for rows with nothing to compare (placeholders, internal work): the actual
// half renders as a muted dash rather than a 0 that would read as "nobody worked it".
function WeekPair({ s, c, miss, noActual = false }) {
  if (!s && !c) return <td className="sched-mgr-td-right sched-rpt-wk" />;
  return (
    <td className="sched-mgr-td-right sched-rpt-wk" style={{ whiteSpace: 'nowrap', ...(miss ? MISS_STYLE[miss] : null) }}>
      {noActual
        ? <span className="sched-rpt-na-text">–</span>
        : <span style={{ fontWeight: 600 }}>{hrs(c) || '0'}</span>}
      <span className="sched-rpt-muted-sm"> / {hrs(s) || '0'}</span>
    </td>
  );
}

function Totals({ r }) {
  const tint = r.miss ? MISS_STYLE[r.miss] : null;
  return (
    <>
      <td className="sched-mgr-td-right sched-rpt-totfirst" style={{ fontWeight: 600 }}>{hrs(r.C) || '0'}</td>
      <td className="sched-mgr-td-right">{hrs(r.S) || '0'}</td>
      <td className="sched-mgr-td-right" style={{ fontWeight: 600, ...(tint || {}) }}
        title={r.miss === 'under' ? 'Actual well under what was scheduled.'
          : r.miss === 'over' ? 'Actual well over what was scheduled.' : undefined}>
        {r.S || r.C ? signed(r.delta) : ''}
      </td>
    </>
  );
}

function ReconcileLine({ rec, who }) {
  if (!rec) return null;
  const ch = rec.charged;
  return (
    <div className="sched-rpt-muted-sm" style={{ lineHeight: 1.6 }}
      title="Everything on the timesheet for these weeks. Only time on jobs RPRS schedules can be compared with the calendar; admin, PTO and other work are listed so the total still adds up.">
      {who && <strong style={{ color: BRAND.deepTeal }}>{who}: </strong>}
      Actual (charged) <strong>{hrs(ch.total) || '0'}h</strong>
      {' = '}RPRS jobs {hrs(ch.rprs) || '0'} + Admin {hrs(ch.admin) || '0'} + PTO {hrs(ch.pto) || '0'}
      {' + '}Other client work {hrs(ch.other) || '0'}
      {' · '}Scheduled <strong>{hrs(rec.scheduled.total) || '0'}h</strong>
      {rec.scheduled.custom > 0 && <> (client {hrs(rec.scheduled.client) || '0'} · internal {hrs(rec.scheduled.custom)})</>}
    </div>
  );
}

export default function SvaReport() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [view, setView] = useUrlState('vview', '', { validate: ['', 'me', 'jobs', 'firm'] });
  const [mode, setMode] = useUrlState('vmode', 'job', { validate: ['job', 'person'] });
  const [role, setRole] = useUrlState('vrole', 'both', { validate: ['both', 'jm', 'jp'] });
  const [weeks, setWeeks] = useUrlState('vweeks', 1, { validate: RANGE_OPTIONS });
  const [asStaff, setAsStaff] = useUrlState('vas', '');
  const [minCharged, setMinCharged] = useUrlState('vmin', 4, {
    validate: (n) => Number.isFinite(n) && n >= 0 && n <= 10000,
  });
  const [budgetOnly, setBudgetOnly] = useUrlState('vbud', false);
  const [hideTiny, setHideTiny] = useUrlState('vtiny', true);
  const [q, setQ] = useUrlState('vq', '');

  // The threshold refetches (it trims the charged-only lists server-side), so debounce it.
  const [minText, setMinText] = useState(String(minCharged));
  useEffect(() => { setMinText(String(minCharged)); }, [minCharged]);
  useEffect(() => {
    const n = Number(minText);
    if (minText === '' || !Number.isFinite(n) || n < 0 || n === minCharged) return undefined;
    const t = setTimeout(() => setMinCharged(n), 400);
    return () => clearTimeout(t);
  }, [minText, minCharged, setMinCharged]);

  const [expanded, setExpanded] = useState(() => new Set());
  const [showCharged, setShowCharged] = useState(false);
  const [showAllCharged, setShowAllCharged] = useState(false);
  const mainSort = useSort(MAIN_SORT, 'client', 'vsort');
  const personSort = useSort(PERSON_SORT, 'S', 'vpsort');
  const chargedSort = useSort(CHARGED_SORT, 'C', 'vcsort');
  const chargedPersonSort = useSort(CHARGED_PERSON_SORT, 'C', 'vcpsort');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchSva({
      view: view || undefined,
      mode,
      role,
      weeks,
      asStaffId: asStaff ? Number(asStaff) : undefined,
      minCharged,
    })
      .then((r) => { if (!cancelled) { setData(r); setError(null); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [view, mode, role, weeks, asStaff, minCharged]);

  const eff = data?.view || null;
  const weekDates = useMemo(() => data?.weeks || [], [data]);
  const rangeStart = data?.rangeStart;
  const needle = q.trim().toLowerCase();
  const matchJob = (r) => !needle || [r.clientCode, r.clientName, r.jobName, r.managerName, r.partnerName]
    .some((s) => String(s || '').toLowerCase().includes(needle))
    // A person's name finds every job they are on — searching "who worked where" is the
    // common case, and without this it silently matched nothing in the job views.
    || (r.staff || []).some((st) => String(st.name || '').toLowerCase().includes(needle));

  // ── Filtered, sorted rows for whichever view came back ─────────────────────
  const mainRows = useMemo(() => {
    const src = eff === 'me' ? (data?.rows || []) : (data?.jobs || []);
    return mainSort.sortRows(src.filter((r) => matchJob(r)
      && (!budgetOnly || (r.budgetHours || 0) >= 20)
      && (!hideTiny || !isTiny(r))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, eff, needle, budgetOnly, hideTiny, mainSort]);

  const people = useMemo(() => personSort.sortRows((data?.people || []).filter((p) =>
    (!needle || String(p.name || '').toLowerCase().includes(needle)
      || p.jobs.some((j) => [j.clientCode, j.clientName, j.jobName].some((s) => String(s || '').toLowerCase().includes(needle))))
    && (!hideTiny || !isTiny(p) || (p.reconcile && p.reconcile.charged.total >= TINY)))), [data, needle, hideTiny, personSort]);

  const internalRows = useMemo(() => (data?.customRows || []).filter((r) =>
    (!needle || String(r.name || '').toLowerCase().includes(needle)) && (!hideTiny || (r.S || 0) >= TINY)),
  [data, needle, hideTiny]);

  // Only the search (global) and this section's own Min hours apply here — the budget
  // toggle sits on the main table and filters only the main table.
  // In By-person mode the server sends PEOPLE here (each expandable to the jobs they charged
  // without being scheduled on); otherwise jobs (each expandable to who charged them).
  const coKind = data?.chargedOnlyKind || 'job';
  const chargedOnly = useMemo(() => {
    const src = data?.chargedOnly || [];
    if (coKind === 'person') {
      return chargedPersonSort.sortRows(src.filter((p) => !needle
        || String(p.name || '').toLowerCase().includes(needle)
        || p.jobs.some((j) => [j.clientCode, j.clientName, j.jobName].some((x) => String(x || '').toLowerCase().includes(needle)))));
    }
    return chargedSort.sortRows(src.filter((r) => matchJob(r)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, coKind, needle, chargedSort, chargedPersonSort]);

  // Same rule as the filters above, so the "(N hidden)" count is exactly what was dropped.
  const tinyHidden = useMemo(() => {
    if (!hideTiny) return 0;
    if (eff !== 'me' && data?.mode === 'person') {
      return (data?.people || []).filter((p) => isTiny(p) && !(p.reconcile && p.reconcile.charged.total >= TINY)).length;
    }
    const src = eff === 'me' ? (data?.rows || []) : (data?.jobs || []);
    return src.filter(isTiny).length;
  }, [data, eff, hideTiny]);

  // Headline figures describe the rows on screen.
  const stats = useMemo(() => {
    const rows = eff && data?.mode === 'person' && eff !== 'me' ? people : mainRows;
    return {
      rows: rows.length,
      S: Math.round(rows.reduce((a, r) => a + (r.S || 0), 0) * 100) / 100,
      C: Math.round(rows.reduce((a, r) => a + (r.C || 0), 0) * 100) / 100,
      under: rows.filter((r) => r.miss === 'under').length,
      over: rows.filter((r) => r.miss === 'over').length,
    };
  }, [eff, data, people, mainRows]);

  const toggleExpand = (k) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  const jobLink = (r) => (
    r.closedInPE && !(r.S > 0)
      ? <span title="Completed in Practice Engine.">{r.jobName}</span>
      : <span className="sched-grid-detail-link" onClick={() => navigate(gridJobHref(r, rangeStart, weeks))}
          title="Open this job on the schedule at these weeks.">{r.jobName}</span>
  );

  // ── CSV: one file, a Section column, two numeric columns per week ──────────
  const exportView = () => {
    const out = [];
    const push = (section, who, r, extra = {}) => out.push({
      section, who, clientCode: r.clientCode || '', clientName: r.clientName || '', item: r.jobName || r.name || '',
      s: r.s || [], c: r.c || [], S: r.S ?? '', C: r.C ?? '', delta: r.delta ?? '', ...extra,
    });
    const subjectName = data?.subject?.name || '';
    if (eff === 'me') {
      mainRows.forEach((r) => push('Main', subjectName, r));
      (data?.customRows || []).forEach((r) => push('Internal work', subjectName, { ...r, C: '', delta: '' }));
      chargedOnly.forEach((r) => push('Charged not scheduled', subjectName, { ...r, S: 0, delta: '' }));
      const rec = data?.reconcile;
      if (rec) {
        [['Scheduled - client jobs', rec.scheduled.client, ''], ['Scheduled - internal work', rec.scheduled.custom, ''],
          ['Charged - RPRS jobs', '', rec.charged.rprs], ['Charged - Admin', '', rec.charged.admin],
          ['Charged - PTO', '', rec.charged.pto], ['Charged - Other client work', '', rec.charged.other]]
          .forEach(([item, S, C]) => out.push({ section: 'Reconcile', who: subjectName, clientCode: '', clientName: '', item, s: [], c: [], S, C, delta: '' }));
      }
    } else if (data?.mode === 'person') {
      people.forEach((p) => {
        p.jobs.forEach((j) => push('Main', p.name, j));
        if (p.reconcile) {
          const rc = p.reconcile;
          out.push({ section: 'Reconcile', who: p.name, clientCode: '', clientName: '', item: 'Timesheet',
            s: [], c: [], S: rc.scheduled.total, C: rc.charged.total, delta: '',
            admin: rc.charged.admin, pto: rc.charged.pto, other: rc.charged.other, rprs: rc.charged.rprs });
        }
      });
      chargedOnly.forEach((p) => p.jobs.forEach((j) => push('Charged not scheduled', p.name, { ...j, s: [], S: 0, delta: '' })));
    } else {
      mainRows.forEach((j) => {
        j.staff.forEach((st) => push(st.notScheduled ? 'Main (not scheduled)' : 'Main', st.name, { ...j, ...st, jobName: j.jobName }));
        j.placeholders.forEach((p) => push('Main (placeholder)', `Placeholder ${p.dept} ${p.level}`, { ...j, s: p.s, c: [], S: p.S, C: '', delta: '' }));
      });
      chargedOnly.forEach((r) => ((r.staff || []).length
        ? r.staff.forEach((st) => push('Charged not scheduled', st.name, { ...r, c: st.c, C: st.C, S: 0, delta: '' }))
        : push('Charged not scheduled', '', { ...r, S: 0, delta: '' })));
    }
    exportCsv('rprs-scheduled-vs-actual', out, [
      { label: 'Section', get: (r) => r.section },
      { label: 'Person', get: (r) => r.who },
      { label: 'Client code', get: (r) => r.clientCode },
      { label: 'Client', get: (r) => r.clientName },
      { label: 'Job / item', get: (r) => r.item },
      ...weekDates.flatMap((w, i) => [
        { label: `Actual wk ${w}`, get: (r) => r.c[i] ?? '' },
        { label: `Scheduled wk ${w}`, get: (r) => r.s[i] ?? '' },
      ]),
      { label: 'Actual total', get: (r) => r.C },
      { label: 'Scheduled total', get: (r) => r.S },
      { label: 'Actual minus scheduled', get: (r) => r.delta },
      { label: 'Charged - RPRS jobs', get: (r) => r.rprs ?? '' },
      { label: 'Charged - Admin', get: (r) => r.admin ?? '' },
      { label: 'Charged - PTO', get: (r) => r.pto ?? '' },
      { label: 'Charged - Other client work', get: (r) => r.other ?? '' },
    ]);
  };

  const weekHeads = weekDates.map((w) => (
    <PlainTh key={w} label={shortDay(w)} className="sched-mgr-th-right sched-rpt-wk"
      title={`Week of ${w}: actual (charged) / scheduled.`} />
  ));
  const shownCharged = showAllCharged ? chargedOnly : chargedOnly.slice(0, CHARGED_ONLY_CAP);

  return (
    <div className="sched-rpt-root">
      <div className="sched-cap-toolbar" style={{ flexWrap: 'wrap', rowGap: 6 }}>
        <span className="sched-cap-toolbar-label">VIEW</span>
        <button className={`sched-mgr-pill${eff === 'me' ? ' sched-mgr-pill--active' : ''}`}
          onClick={() => setView('me')}
          title="Your own scheduled time against what you charged.">{data?.subject && !data.subject.self ? 'Person' : 'Me'}</button>
        {data?.canLead && (
          <button className={`sched-mgr-pill${eff === 'jobs' ? ' sched-mgr-pill--active' : ''}`}
            onClick={() => setView('jobs')} title="Jobs where you are job manager or job partner.">Jobs I lead</button>
        )}
        {data?.isSuper && (
          <button className={`sched-mgr-pill${eff === 'firm' ? ' sched-mgr-pill--active' : ''}`}
            onClick={() => setView('firm')} title="Every job and person in the firm.">Firm</button>
        )}

        {(eff === 'jobs' || eff === 'firm') && (
          <>
            <span style={{ width: 12 }} />
            <span className="sched-cap-toolbar-label">BY</span>
            <button className={`sched-mgr-pill${mode === 'job' ? ' sched-mgr-pill--active' : ''}`}
              onClick={() => setMode('job')} title="One row per job, expandable to who worked on it.">Job</button>
            <button className={`sched-mgr-pill${mode === 'person' ? ' sched-mgr-pill--active' : ''}`}
              onClick={() => setMode('person')} title="One row per person, expandable to their jobs.">Person</button>
          </>
        )}
        {eff === 'jobs' && (
          <>
            <span style={{ width: 12 }} />
            <span className="sched-cap-toolbar-label">ROLE</span>
            {[['both', 'JM + JP'], ['jm', 'JM'], ['jp', 'JP']].map(([k, label]) => (
              <button key={k} className={`sched-mgr-pill${role === k ? ' sched-mgr-pill--active' : ''}`}
                onClick={() => setRole(k)}>{label}</button>
            ))}
          </>
        )}

        <span style={{ width: 12 }} />
        <span className="sched-cap-toolbar-label" title="Always ends with the most recent full week.">RANGE</span>
        {RANGE_OPTIONS.map((w) => (
          <button key={w} className={`sched-mgr-pill${weeks === w ? ' sched-mgr-pill--active' : ''}`}
            onClick={() => setWeeks(w)}>{RANGE_LABEL[w]}</button>
        ))}

        {data?.isSuper && (
          <>
            <span style={{ width: 12 }} />
            <span className="sched-cap-toolbar-label" title="See the report exactly as this person sees it.">VIEW AS</span>
            <select className="sched-rpt-bsel" value={asStaff} onChange={(e) => setAsStaff(e.target.value)}>
              <option value="">Me</option>
              {(data.staffList || []).map((s) => (
                <option key={s.staffId} value={String(s.staffId)}>{s.name}{s.isActive ? '' : ' (inactive)'}</option>
              ))}
            </select>
          </>
        )}

        <input className="sched-rpt-btext" style={{ marginLeft: 'auto', minWidth: 170 }}
          placeholder="Search client, job, person…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search" />
        {data && (
          <button className="sched-rpt-csv" onClick={exportView}
            title="Download what is shown, two numeric columns per week.">CSV</button>
        )}
      </div>

      {error &&<div className="sched-rpt-error">Couldn&rsquo;t load: {error}</div>}
      {loading && <div className="sched-rpt-muted">Loading&hellip;</div>}

      {!loading && !error && data && (
        <>
          {data.weekOpen && (
            <div className="sched-rpt-warn">
              <strong>In progress:</strong> time entry for the week of {shortDay(weekDates[weekDates.length - 1])} closes
              Monday at 1 PM &mdash; charged hours for that week may still change.
            </div>
          )}

          <div className="sched-rpt-summary">
            <div className="sched-rpt-statgroup">
              <div className="sched-rpt-statgroup-l">
                {shortDay(data.rangeStart)} &ndash; {shortDay(data.rangeEnd)}
                {data.subject && !data.subject.self && data.subject.name ? ` · ${data.subject.name}` : ''}
              </div>
              <div className="sched-rpt-statrow">
                <div className="sched-rpt-stat" title="Hours actually charged on the rows shown.">
                  <div className="sched-rpt-stat-n">{stats.C}</div>
                  <div className="sched-rpt-stat-l">Actual</div>
                </div>
                <div className="sched-rpt-stat" title="Hours on the calendar for those same rows.">
                  <div className="sched-rpt-stat-n">{stats.S}</div>
                  <div className="sched-rpt-stat-l">Scheduled</div>
                </div>
                <div className="sched-rpt-stat" title="Rows where the actual was well under what was scheduled (4h or more, and at least 25%).">
                  <div className="sched-rpt-stat-n" style={{ color: stats.under ? '#B91C1C' : undefined }}>{stats.under}</div>
                  <div className="sched-rpt-stat-l">Under</div>
                </div>
                <div className="sched-rpt-stat" title="Rows where the actual was well over what was scheduled (4h or more, and at least 25%).">
                  <div className="sched-rpt-stat-n" style={{ color: stats.over ? '#B45309' : undefined }}>{stats.over}</div>
                  <div className="sched-rpt-stat-l">Over</div>
                </div>
              </div>
            </div>
            <div className="sched-rpt-summary-note">
              Each week shows <strong>actual (charged) / scheduled</strong>. Δ is actual minus scheduled; red is well under,
              amber well over. <strong>Scheduled</strong> is what the calendar shows now for those days.
              Only jobs RPRS schedules are compared &mdash; admin, PTO and other work appear in the timesheet line.
            </div>
          </div>

          {eff === 'me' && (
            <div style={{ margin: '0 0 10px' }}>
              <ReconcileLine rec={data.reconcile} who={data.subject && !data.subject.self ? data.subject.name : null} />
            </div>
          )}

          {/* ── Main table ─────────────────────────────────────────────────── */}
          {/* Each section carries its OWN filters, right above it — a filter in the top
              toolbar would read as applying to everything. The top toolbar keeps only what
              truly is global: view, grouping, role, range, view-as and search. */}
          <div className="sched-rpt-sechead">
            <div className="sched-rpt-sechead-title">
              {eff === 'me' ? 'Jobs you were scheduled on' : data.mode === 'job' ? 'Scheduled jobs' : 'People'}
            </div>
            <div className="sched-rpt-sechead-tools">
              {(eff === 'me' || data.mode === 'job') && (
                <label className="sched-rpt-check" title="Only jobs with a total budget of at least 20 hours, like the old weekly email.">
                  <input type="checkbox" checked={budgetOnly} onChange={(e) => setBudgetOnly(e.target.checked)} />
                  {' '}Budget ≥ 20h
                </label>
              )}
              <label className="sched-rpt-check" title="Hide rows where both scheduled and charged are under 1 hour.">
                <input type="checkbox" checked={hideTiny} onChange={(e) => setHideTiny(e.target.checked)} />
                {' '}Hide tiny rows{hideTiny && tinyHidden > 0 ? ` (${tinyHidden} hidden)` : ''}
              </label>
            </div>
          </div>
          {eff === 'me' || data.mode === 'job' ? (
            <div className="sched-mgr-table-wrap sched-rpt-scroll">
              <table className="sched-mgr-table sched-rpt-table">
                <thead>
                  <tr className="sched-rpt-colrow">
                    {eff !== 'me' && <PlainTh label="" title="Show who worked on the job." />}
                    <SortableTh sort={mainSort} sortKey="client" label="Code" title="Client code." />
                    <SortableTh sort={mainSort} sortKey="clientName" label="Client" title="Client name." />
                    <SortableTh sort={mainSort} sortKey="job" label="Job" title="Click to open the job on the schedule." />
                    <SortableTh sort={mainSort} sortKey="jm" label="JM / JP" title="Job manager and job partner." />
                    {weekHeads}
                    <SortableTh sort={mainSort} sortKey="C" label="Actual" className="sched-mgr-th-right sched-rpt-totfirst" title="Hours actually charged across the weeks shown." />
                    <SortableTh sort={mainSort} sortKey="S" label="Sched" className="sched-mgr-th-right" title="Scheduled hours across the weeks shown." />
                    <SortableTh sort={mainSort} sortKey="delta" label="Δ" className="sched-mgr-th-right" title="Actual minus scheduled." />
                    {eff !== 'me' && <SortableTh sort={mainSort} sortKey="budget" label="Budget" className="sched-mgr-th-right" title="The job's budgeted hours." />}
                  </tr>
                </thead>
                <tbody>
                  {mainRows.map((r) => {
                    const key = `j${r.jobId}`;
                    const open = expanded.has(key);
                    return (
                      <Fragment key={key}>
                        <tr>
                          {eff !== 'me' && (
                            <td style={{ width: 22 }}>
                              <button className="sched-rpt-disclose" style={{ padding: 0 }} onClick={() => toggleExpand(key)}
                                title={open ? 'Hide who worked on it' : 'Show who worked on it'}>{open ? '▾' : '▸'}</button>
                            </td>
                          )}
                          <td className="sched-rpt-muted-sm">{r.clientCode}</td>
                          <td>{r.clientName}</td>
                          <td>
                            {jobLink(r)}
                            {r.noPeLink && <span className="sched-rpt-chip" style={{ background: '#E5E7EB', color: '#374151', marginLeft: 6 }}
                              title="This job isn't linked to Practice Engine, so no charged time can be matched to it.">No PE link</span>}
                          </td>
                          <td className="sched-rpt-muted-sm">
                            {r.managerName || '—'}
                            {r.partnerName && r.partnerName !== r.managerName && <><br />{r.partnerName}</>}
                          </td>
                          {weekDates.map((w, i) => <WeekPair key={w} s={r.s[i]} c={r.c[i]} miss={r.missWk[i]} />)}
                          <Totals r={r} />
                          {eff !== 'me' && <td className="sched-mgr-td-right sched-rpt-muted-sm">{hrs(r.budgetHours) || '—'}</td>}
                        </tr>
                        {open && eff !== 'me' && r.staff.filter((st) => !hideTiny || !isTiny(st)).map((st) => (
                          <tr key={`${key}_s${st.staffId ?? 'u'}`} style={{ background: '#F9FAFB' }}>
                            <td />
                            <td colSpan={4} style={{ paddingLeft: 18 }}>
                              {st.staffId != null
                                ? <span className="sched-grid-detail-link" onClick={() => navigate(`/schedule/employee/${st.staffId}`)}>{st.name}</span>
                                : <span className="sched-rpt-muted-sm">{st.name}</span>}
                              <span className="sched-rpt-muted-sm">{st.level ? ` · ${st.level}` : ''}</span>
                              {st.notScheduled && <span className="sched-rpt-chip" style={{ background: '#E0F2FE', color: '#075985', marginLeft: 6 }}
                                title="Charged time on this job without being scheduled on it.">Not scheduled</span>}
                              {!st.isActive && <span className="sched-rpt-chip" style={{ background: '#FECACA', color: '#7F1D1D', marginLeft: 6 }}>Inactive</span>}
                            </td>
                            {weekDates.map((w, i) => <WeekPair key={w} s={st.s[i]} c={st.c[i]} miss={st.missWk[i]} />)}
                            <Totals r={st} />
                            <td />
                          </tr>
                        ))}
                        {open && eff !== 'me' && r.placeholders.map((p) => (
                          <tr key={`${key}_p${p.placeholderId}`} style={{ background: '#FFFBEB' }}>
                            <td />
                            <td colSpan={4} style={{ paddingLeft: 18, fontStyle: 'italic', color: '#B45309' }}
                              title="Hours held for a level, not yet assigned to a person. Nothing can be charged against a placeholder.">
                              Placeholder · {p.dept} · {p.level}
                            </td>
                            {weekDates.map((w, i) => <WeekPair key={w} s={p.s[i]} c={0} miss={null} noActual />)}
                            <td className="sched-mgr-td-right sched-rpt-totfirst sched-rpt-na" title="Nothing can be charged against a placeholder.">—</td>
                            <td className="sched-mgr-td-right" style={{ fontStyle: 'italic', color: '#B45309' }}>{hrs(p.S)}</td>
                            <td colSpan={2} />
                          </tr>
                        ))}
                      </Fragment>
                    );
                  })}
                  {mainRows.length === 0 && (
                    <tr><td colSpan={(eff === 'me' ? 7 : 9) + weekDates.length} className="sched-rpt-muted">
                      {eff === 'me'
                        ? 'Nothing was scheduled on client jobs in these weeks.'
                        : 'No scheduled jobs match these filters.'}
                    </td></tr>
                  )}
                  {/* Internal (custom-job) work lives in THIS table, not its own, so its week
                      columns line up with the jobs above. Charged and Δ are greyed: there is no
                      charged figure to compare (that time lands under Admin on the timesheet). */}
                  {eff === 'me' && internalRows.length > 0 && (
                    <>
                      <tr className="sched-rpt-secrow">
                        <td colSpan={7 + weekDates.length}
                          title="Time scheduled on internal work such as CPE or the EBP team. There is nothing in the timesheet to compare it with directly; it shows up under Admin in the timesheet line.">
                          Internal work &mdash; scheduled only, no comparison
                        </td>
                      </tr>
                      {internalRows.map((r) => (
                        <tr key={`c${r.customJobId}`}>
                          <td colSpan={4}>{r.name}</td>
                          {weekDates.map((w, i) => (
                            <td key={w} className="sched-mgr-td-right sched-rpt-wk" style={{ whiteSpace: 'nowrap' }}>
                              {r.s[i] ? <><span className="sched-rpt-na-text">–</span><span className="sched-rpt-muted-sm"> / {hrs(r.s[i])}</span></> : ''}
                            </td>
                          ))}
                          <td className="sched-mgr-td-right sched-rpt-totfirst sched-rpt-na" title="Not compared — internal work has no charged figure to match.">—</td>
                          <td className="sched-mgr-td-right">{hrs(r.S)}</td>
                          <td className="sched-mgr-td-right sched-rpt-na" title="Not compared — internal work has no charged figure to match.">—</td>
                        </tr>
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="sched-mgr-table-wrap sched-rpt-scroll">
              <table className="sched-mgr-table sched-rpt-table">
                <thead>
                  <tr className="sched-rpt-colrow">
                    <PlainTh label="" title="Show this person's jobs." />
                    <SortableTh sort={personSort} sortKey="name" label="Person" title="Someone scheduled or charging on these jobs." />
                    <SortableTh sort={personSort} sortKey="level" label="Level" title="Staff level." />
                    {weekHeads}
                    <SortableTh sort={personSort} sortKey="C" label="Actual" className="sched-mgr-th-right sched-rpt-totfirst"
                      title={eff === 'jobs' ? 'Hours actually charged on your jobs.' : 'Hours actually charged on jobs RPRS schedules.'} />
                    <SortableTh sort={personSort} sortKey="S" label="Sched" className="sched-mgr-th-right"
                      title={eff === 'jobs' ? 'Scheduled hours on your jobs.' : 'Scheduled hours on client jobs.'} />
                    <SortableTh sort={personSort} sortKey="delta" label="Δ" className="sched-mgr-th-right" title="Actual minus scheduled." />
                    {eff === 'firm' && <SortableTh sort={personSort} sortKey="sheet" label="Timesheet" className="sched-mgr-th-right"
                      title="Everything charged in these weeks, including admin, PTO and other work. Expand the row for the breakdown." />}
                  </tr>
                </thead>
                <tbody>
                  {people.map((p) => {
                    const key = `p${p.staffId}`;
                    const open = expanded.has(key);
                    return (
                      <Fragment key={key}>
                        <tr>
                          <td style={{ width: 22 }}>
                            <button className="sched-rpt-disclose" style={{ padding: 0 }} onClick={() => toggleExpand(key)}
                              title={open ? 'Hide their jobs' : 'Show their jobs'}>{open ? '▾' : '▸'}</button>
                          </td>
                          <td>
                            <span className="sched-grid-detail-link" onClick={() => navigate(`/schedule/employee/${p.staffId}`)}>{p.name}</span>
                            {!p.isActive && <span className="sched-rpt-chip" style={{ background: '#FECACA', color: '#7F1D1D', marginLeft: 6 }}>Inactive</span>}
                            {p.noPeLink && <span className="sched-rpt-chip" style={{ background: '#E5E7EB', color: '#374151', marginLeft: 6 }}
                              title="Not linked to Practice Engine, so no charged time can be matched.">No PE link</span>}
                          </td>
                          <td className="sched-rpt-muted-sm">{p.level || '—'}</td>
                          {weekDates.map((w, i) => <WeekPair key={w} s={p.s[i]} c={p.c[i]} miss={p.missWk[i]} />)}
                          <Totals r={p} />
                          {eff === 'firm' && <td className="sched-mgr-td-right sched-rpt-muted-sm">{hrs(p.reconcile?.charged?.total) || '0'}</td>}
                        </tr>
                        {open && eff === 'firm' && p.reconcile && (
                          <tr style={{ background: '#F9FAFB' }}>
                            <td />
                            <td colSpan={2 + weekDates.length + 4} style={{ paddingLeft: 18 }}>
                              <ReconcileLine rec={p.reconcile} />
                            </td>
                          </tr>
                        )}
                        {open && p.jobs.map((j) => (
                          <tr key={`${key}_${j.jobId}`} style={{ background: '#F9FAFB' }}>
                            <td />
                            <td colSpan={2} style={{ paddingLeft: 18 }}>
                              {j.closedInPE && !(j.S > 0)
                                ? <span>{j.clientName} · {j.jobName}</span>
                                : <span className="sched-grid-detail-link" onClick={() => navigate(gridJobHref(j, rangeStart, weeks))}>{j.clientName} · {j.jobName}</span>}
                            </td>
                            {weekDates.map((w, i) => <WeekPair key={w} s={j.s[i]} c={j.c[i]} miss={null} />)}
                            <td className="sched-mgr-td-right sched-rpt-totfirst">{hrs(j.C) || '0'}</td>
                            <td className="sched-mgr-td-right">{hrs(j.S) || '0'}</td>
                            <td colSpan={eff === 'firm' ? 2 : 1} />
                          </tr>
                        ))}
                      </Fragment>
                    );
                  })}
                  {people.length === 0 && (
                    <tr><td colSpan={7 + weekDates.length} className="sched-rpt-muted">No one matches these filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}


          {/* ── Charged, never scheduled (collapsed) ─────────────────────────── */}
          {/* Follows the main table's grouping: by job (expand -> who charged it) or, in
              By-person mode, by person (expand -> the jobs they charged without being
              scheduled on). Min hours applies to each row's total. */}
          {(data.chargedOnlySummary?.count || 0) > 0 && (
            <div style={{ marginTop: 16 }}>
              <div className="sched-rpt-sechead">
                <button className="sched-rpt-disclose sched-rpt-sechead-title" onClick={() => setShowCharged((v) => !v)}
                  title={coKind === 'person'
                    ? 'Time people charged to jobs they were not scheduled on, for these weeks. Only jobs RPRS schedules are listed.'
                    : 'Time charged on jobs nobody scheduled for these weeks. Only jobs RPRS schedules are listed.'}>
                  {showCharged ? '▾' : '▸'} Charged, not scheduled: {chargedOnly.length}{' '}
                  {coKind === 'person' ? (chargedOnly.length === 1 ? 'person' : 'people') : (chargedOnly.length === 1 ? 'job' : 'jobs')} shown
                  {' '}<span className="sched-rpt-muted-sm" style={{ fontWeight: 400 }}>
                    of {data.chargedOnlySummary.count} · {data.chargedOnlySummary.hours}h in total
                  </span>
                </button>
                <div className="sched-rpt-sechead-tools">
                  <span className="sched-rpt-bfield">
                    <label className="sched-rpt-blabel"
                      title={coKind === 'person'
                        ? "Only list people whose time charged without being scheduled adds up to at least this many hours. 0 lists everyone."
                        : 'Only list jobs where the time charged without being scheduled adds up to at least this many hours. 0 lists everything.'}>
                      Min hours
                    </label>
                    <input className="sched-rpt-btext sched-rpt-bnum" type="number" min="0" step="1"
                      value={minText} onChange={(e) => setMinText(e.target.value)} aria-label="Minimum charged hours" />
                  </span>
                </div>
              </div>
              {showCharged && (
                <div className="sched-mgr-table-wrap sched-rpt-scroll">
                  <table className="sched-mgr-table sched-rpt-table">
                    {coKind === 'person' ? (
                      <>
                        <thead>
                          <tr className="sched-rpt-colrow">
                            <PlainTh label="" title="Show the jobs they charged." />
                            <SortableTh sort={chargedPersonSort} sortKey="name" label="Person" title="Someone who charged time to a job they were not scheduled on." />
                            <SortableTh sort={chargedPersonSort} sortKey="level" label="Level" title="Staff level." />
                            {weekDates.map((w) => <PlainTh key={w} label={shortDay(w)} className="sched-mgr-th-right sched-rpt-wk" title={`Actual (charged) in the week of ${w}.`} />)}
                            <SortableTh sort={chargedPersonSort} sortKey="C" label="Actual" className="sched-mgr-th-right sched-rpt-totfirst" title="Hours charged without being scheduled, across the weeks shown." />
                            <SortableTh sort={chargedPersonSort} sortKey="jobs" label="Jobs" className="sched-mgr-th-right" title="How many such jobs." />
                          </tr>
                        </thead>
                        <tbody>
                          {shownCharged.map((p) => {
                            const key = `co-p${p.staffId}`;
                            const open = expanded.has(key);
                            return (
                              <Fragment key={key}>
                                <tr>
                                  <td style={{ width: 22 }}>
                                    <button className="sched-rpt-disclose" style={{ padding: 0 }} onClick={() => toggleExpand(key)}
                                      title={open ? 'Hide their jobs' : 'Show the jobs they charged'}>{open ? '▾' : '▸'}</button>
                                  </td>
                                  <td>
                                    <span className="sched-grid-detail-link" onClick={() => navigate(`/schedule/employee/${p.staffId}`)}>{p.name}</span>
                                    {!p.isActive && <span className="sched-rpt-chip" style={{ background: '#FECACA', color: '#7F1D1D', marginLeft: 6 }}>Inactive</span>}
                                  </td>
                                  <td className="sched-rpt-muted-sm">{p.level || '—'}</td>
                                  {weekDates.map((w, i) => <td key={w} className="sched-mgr-td-right sched-rpt-wk">{hrs(p.c[i])}</td>)}
                                  <td className="sched-mgr-td-right sched-rpt-totfirst" style={{ fontWeight: 600 }}>{hrs(p.C)}</td>
                                  <td className="sched-mgr-td-right sched-rpt-muted-sm">{p.jobCount}</td>
                                </tr>
                                {open && p.jobs.map((j) => (
                                  <tr key={`${key}_${j.jobId}`} style={{ background: '#F9FAFB' }}>
                                    <td />
                                    <td colSpan={2} style={{ paddingLeft: 18 }}>
                                      {j.closedInPE
                                        ? <span>{j.clientName} · {j.jobName}</span>
                                        : <span className="sched-grid-detail-link" onClick={() => navigate(gridJobHref(j, rangeStart, weeks))}>{j.clientName} · {j.jobName}</span>}
                                    </td>
                                    {weekDates.map((w, i) => <td key={w} className="sched-mgr-td-right sched-rpt-wk">{hrs(j.c[i])}</td>)}
                                    <td className="sched-mgr-td-right sched-rpt-totfirst">{hrs(j.C)}</td>
                                    <td />
                                  </tr>
                                ))}
                              </Fragment>
                            );
                          })}
                          {chargedOnly.length === 0 && (
                            <tr><td colSpan={5 + weekDates.length} className="sched-rpt-muted">
                              No one at or above {data.minCharged}h. Lower the minimum to see smaller amounts.
                            </td></tr>
                          )}
                        </tbody>
                      </>
                    ) : (
                      <>
                        <thead>
                          <tr className="sched-rpt-colrow">
                            {eff !== 'me' && <PlainTh label="" title="Show who charged time to the job." />}
                            <SortableTh sort={chargedSort} sortKey="client" label="Code" title="Client code." />
                            <PlainTh label="Client" title="Client name." />
                            <SortableTh sort={chargedSort} sortKey="job" label="Job" title="The job charged to." />
                            {weekDates.map((w) => <PlainTh key={w} label={shortDay(w)} className="sched-mgr-th-right sched-rpt-wk" title={`Actual (charged) in the week of ${w}.`} />)}
                            <SortableTh sort={chargedSort} sortKey="C" label="Actual" className="sched-mgr-th-right sched-rpt-totfirst" title="Hours charged across the weeks shown." />
                          </tr>
                        </thead>
                        <tbody>
                          {shownCharged.map((r) => {
                            const key = `co-j${r.jobId}`;
                            const open = expanded.has(key);
                            return (
                              <Fragment key={key}>
                                <tr>
                                  {eff !== 'me' && (
                                    <td style={{ width: 22 }}>
                                      {(r.staff || []).length > 0 && (
                                        <button className="sched-rpt-disclose" style={{ padding: 0 }} onClick={() => toggleExpand(key)}
                                          title={open ? 'Hide who charged it' : `Show who charged it (${r.staff.length})`}>{open ? '▾' : '▸'}</button>
                                      )}
                                    </td>
                                  )}
                                  <td className="sched-rpt-muted-sm">{r.clientCode}</td>
                                  <td>{r.clientName}</td>
                                  <td>
                                    {jobLink(r)}
                                    {eff !== 'me' && r.staffCount > 0 && <span className="sched-rpt-muted-sm"> · {r.staffCount} {r.staffCount === 1 ? 'person' : 'people'}</span>}
                                  </td>
                                  {weekDates.map((w, i) => <td key={w} className="sched-mgr-td-right sched-rpt-wk">{hrs(r.c[i])}</td>)}
                                  <td className="sched-mgr-td-right sched-rpt-totfirst" style={{ fontWeight: 600 }}>{hrs(r.C)}</td>
                                </tr>
                                {open && (r.staff || []).map((st) => (
                                  <tr key={`${key}_s${st.staffId ?? 'u'}`} style={{ background: '#F9FAFB' }}>
                                    <td />
                                    <td colSpan={3} style={{ paddingLeft: 18 }}>
                                      {st.staffId != null
                                        ? <span className="sched-grid-detail-link" onClick={() => navigate(`/schedule/employee/${st.staffId}`)}>{st.name}</span>
                                        : <span className="sched-rpt-muted-sm">{st.name}</span>}
                                      <span className="sched-rpt-muted-sm">{st.level ? ` · ${st.level}` : ''}</span>
                                      {!st.isActive && <span className="sched-rpt-chip" style={{ background: '#FECACA', color: '#7F1D1D', marginLeft: 6 }}>Inactive</span>}
                                    </td>
                                    {weekDates.map((w, i) => <td key={w} className="sched-mgr-td-right sched-rpt-wk">{hrs(st.c[i])}</td>)}
                                    <td className="sched-mgr-td-right sched-rpt-totfirst">{hrs(st.C)}</td>
                                  </tr>
                                ))}
                              </Fragment>
                            );
                          })}
                          {chargedOnly.length === 0 && (
                            <tr><td colSpan={(eff === 'me' ? 4 : 5) + weekDates.length} className="sched-rpt-muted">
                              Nothing at or above {data.minCharged}h. Lower the minimum to see smaller amounts.
                            </td></tr>
                          )}
                        </tbody>
                      </>
                    )}
                  </table>
                  {chargedOnly.length > CHARGED_ONLY_CAP && !showAllCharged && (
                    <button className="sched-rpt-disclose" onClick={() => setShowAllCharged(true)}>
                      Show all {chargedOnly.length}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {eff !== 'me' && data.unmappedHours > 0 && (
            <div className="sched-rpt-muted-sm" style={{ marginTop: 10 }}
              title="Charged by people Practice Engine knows but RPRS has no staff record for. Counted in job totals, not shown per person.">
              {data.unmappedHours}h charged by people with no RPRS staff record are included in job totals.
            </div>
          )}
        </>
      )}
    </div>
  );
}
